import { Component, OnDestroy, OnInit } from '@angular/core';
import {
    AuctionCatalogsService,
    GetAdminInvoices200Response,
    InvoiceListItem,
} from '@viewbid/ts-angular-client';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { ProductService } from '@/shared/services/product.service';
import { InvoiceStatus } from '../../enums/invoice-statuses';
import { invoiceStatusLabel } from '../../utils/invoice-status-label.util';
import {
    Subject,
    debounceTime,
    distinctUntilChanged,
    map,
    takeUntil,
} from 'rxjs';
import { DeviceCheckService } from 'src/app/viewbid/shared/services/device-check.service';
import {
    navigateProfilePaging,
    ProfilePaging,
    resolveProfilePaging,
} from 'src/app/viewbid/shared/utils/profile-paging.util';
import {
    fmtDate,
    VbInvoiceRowStatus,
    VbInvoiceTableRow,
    VbLegendItem,
    VbSnackbarService,
} from '@viewbid/ui';
import { TranslateService } from '@ngx-translate/core';

@Component({
    selector: 'vb-user-invoices',
    templateUrl: './user-invoices.component.html',
    styleUrls: ['./user-invoices.component.scss'],
})
export class UserInvoicesComponent implements OnInit, OnDestroy {
    constructor(
        private route: ActivatedRoute,
        public productService: ProductService,
        private router: Router,
        private auctionCatalogsService: AuctionCatalogsService,
        private deviceCheckService: DeviceCheckService,
        private snackbarService: VbSnackbarService,
        private translate: TranslateService,
    ) {}
    userInvoices: InvoiceListItem[] = [];
    /** Presentation rows for <vb-invoice-table>, derived from userInvoices. */
    public rows: VbInvoiceTableRow[] = [];
    /** Row id → source invoice, so table events dispatch on the real record. */
    private invoiceByRowId: Map<string, InvoiceListItem> = new Map();
    public paginate: any = {};
    public isLoading: boolean = true;
    filterParam?: string;
    public pageNo: number = 1;
    public pageSize: number = 10;
    private readonly defaultPageSize: number = this.pageSize;
    public readonly pageSizeOptions: ReadonlyArray<number> = [10, 25, 50];
    public totalCount: number = 0;
    public unableToLoad: boolean = false;
    searchText?: string;
    private searchedInvoice: Subject<string> = new Subject();
    public get invoiceStatus(): typeof InvoiceStatus {
        return InvoiceStatus;
    }
    private mobile$ = new Subject<void>();
    private destroy$ = new Subject<void>();
    public isMobile: boolean = false;

    /** Footer legend — the design system's amber/neutral/green state palette
     *  (the old per-status rainbow is remapped onto three buckets). */
    public readonly legendItems: VbLegendItem[] = [
        { tone: 'warn', label: 'Action required' },
        { tone: 'neutral', label: 'In progress' },
        { tone: 'positive', label: 'Complete' },
    ];

    ngOnInit(): void {
        this.route.queryParams
            .pipe(
                map((params: Params): ProfilePaging => {
                    navigateProfilePaging(
                        this.router,
                        this.route,
                        params,
                        this.defaultPageSize,
                        true,
                    );
                    return resolveProfilePaging(params, this.defaultPageSize);
                }),
                distinctUntilChanged(
                    (previous: ProfilePaging, current: ProfilePaging) =>
                        previous.page === current.page &&
                        previous.pageSize === current.pageSize,
                ),
                takeUntil(this.destroy$),
            )
            .subscribe((paging: ProfilePaging) => {
                this.pageNo = paging.page;
                this.pageSize = paging.pageSize;
                this.loadUserInvoices(this.searchText || undefined);
            });

        this.searchedInvoice
            .pipe(
                debounceTime(500),
                distinctUntilChanged(),
                takeUntil(this.destroy$),
            )
            .subscribe((searchText: string) => {
                if (
                    !navigateProfilePaging(
                        this.router,
                        this.route,
                        { page: 1, pageSize: this.pageSize },
                        this.defaultPageSize,
                    )
                ) {
                    this.loadUserInvoices(searchText || undefined);
                }
            });
        this.deviceCheckService.isMobile$
            .pipe(takeUntil(this.mobile$))
            .subscribe((isMobile: boolean) => {
                this.isMobile = isMobile;
            });

        this.deviceCheckService.isSlow().then((isSlow: boolean) => {
            if (isSlow) {
                this.snackbarService.system(
                    this.translate.instant(
                        'Notifications.slowConnectionDetected',
                    ),
                );
            }
        });
    }

    private loadUserInvoices(invoiceNumber?: string): void {
        const paging: ProfilePaging = {
            page: this.pageNo,
            pageSize: this.pageSize,
        };
        this.isLoading = true;
        this.auctionCatalogsService
            .getUserInvoices(paging.page, paging.pageSize, invoiceNumber)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (response: GetAdminInvoices200Response) => {
                    if (
                        paging.page !== this.pageNo ||
                        paging.pageSize !== this.pageSize
                    ) {
                        return;
                    }
                    const correctedPaging: ProfilePaging = resolveProfilePaging(
                        paging,
                        this.defaultPageSize,
                        response.totalCount ?? 0,
                    );
                    if (correctedPaging.page !== paging.page) {
                        navigateProfilePaging(
                            this.router,
                            this.route,
                            correctedPaging,
                            this.defaultPageSize,
                            true,
                        );
                        return;
                    }
                    this.isLoading = false;
                    this.userInvoices = response.data;
                    if (this.userInvoices.length > 0) {
                        this.totalCount = response.totalCount ?? 0;
                        this.paginate = this.productService.getPager(
                            response.totalCount ?? 0,
                            paging.page,
                            paging.pageSize,
                            false,
                        );
                    } else {
                        this.userInvoices = [];
                        this.unableToLoad = true;
                        this.totalCount = 0;
                        this.paginate = this.productService.getPager(
                            0,
                            0,
                            paging.pageSize,
                            false,
                        );
                        this.paginate.currentPage = 0;
                    }
                    this.buildRows();
                },
                error: () => {
                    if (
                        paging.page !== this.pageNo ||
                        paging.pageSize !== this.pageSize
                    ) {
                        return;
                    }
                    this.isLoading = false;
                    this.unableToLoad = true;
                },
            });
    }

    /** Map the API invoices onto vb-invoice-table presentation rows. */
    private buildRows(): void {
        this.invoiceByRowId.clear();
        this.rows = this.userInvoices.map((invoice: InvoiceListItem) => {
            const id: string = invoice.id ?? '';
            this.invoiceByRowId.set(id, invoice);
            const status: VbInvoiceRowStatus = this.statusBucket(invoice);
            return {
                id,
                number: String(invoice.number ?? ''),
                // Amber row accent: the buyer must act (review bucket). The
                // warning triangle is reserved for forfeited invoices — same
                // rule + tooltip as the old table's forfeited icon.
                needsAttention: status === 'review',
                warningLabel: this.forfeitLabel(invoice),
                seller: 'Viewbid',
                location: invoice.location?.name ?? '',
                dateLabel: fmtDate(invoice.createdOn),
                amountDue: invoice.amountDue ?? 0,
                status,
                statusLabel: this.statusLabel(invoice),
                actionLabel: this.actionLabel(invoice),
            };
        });
    }

    /** Plain-language chip text per API status — the shared vocabulary in
     *  invoice-status-label.util (also used by the view-invoice history).
     *  Tone still comes from statusBucket(); unknown statuses fall back to
     *  the neutral bucket's legend label. */
    private statusLabel(invoice: InvoiceListItem): string {
        return invoiceStatusLabel(invoice.statusType ?? undefined);
    }

    /** Bucket the app's eight invoice statuses onto the design system's three
     *  states (amber review / neutral assembly / green complete):
     *  - review: PendingBuyerReview and PendingBuyerShippingReview
     *    (the buyer has to act)
     *  - assembly: all quote, assembly, and pickup states
     *    (in progress on Viewbid's side)
     *  - complete: Completed */
    private statusBucket(invoice: InvoiceListItem): VbInvoiceRowStatus {
        switch (invoice.statusType) {
            case InvoiceStatus.PendingBuyerReview:
            case InvoiceStatus.PendingBuyerShippingReview:
                return 'review';
            case InvoiceStatus.Completed:
                return 'complete';
            default:
                return 'assembly';
        }
    }

    /** Single action per row, mirroring the old template's button conditions
     *  (first match wins). Destinations are unchanged — see onTableAction. */
    private actionLabel(invoice: InvoiceListItem): string {
        if (invoice.statusType === InvoiceStatus.PendingBuyerReview) {
            return 'Finalize invoice';
        }
        if (invoice.statusType === InvoiceStatus.PendingBuyerShippingReview) {
            return 'View shipping quote';
        }
        if (invoice.amountDue && invoice.amountDue > 0) {
            return 'Pay invoice';
        }
        if (invoice.amountPaid && invoice.amountPaid > 0) {
            return 'View payments';
        }
        return 'View';
    }

    /** Whole-row activation — same destination as the old invoice-number
     *  link: ../view-invoice/:id. */
    onTableRow(row: VbInvoiceTableRow): void {
        this.viewInvoiceDetails(row.id);
    }

    /** Trailing action button — dispatches to the pre-redesign destination
     *  for the row's state (conditions mirror actionLabel above). */
    onTableAction(row: VbInvoiceTableRow): void {
        const invoice: InvoiceListItem | undefined = this.invoiceByRowId.get(
            row.id,
        );
        if (!invoice) return;
        if (invoice.statusType === InvoiceStatus.PendingBuyerReview) {
            this.goToInvoiceActions(invoice);
            return;
        }
        if (invoice.statusType === InvoiceStatus.PendingBuyerShippingReview) {
            this.onViewShippingQuote(invoice.id);
            return;
        }
        if (invoice.amountDue && invoice.amountDue > 0) {
            this.goToInvoiceActions(invoice);
            return;
        }
        if (invoice.amountPaid && invoice.amountPaid > 0) {
            this.goToPaymentsList(invoice);
            return;
        }
        this.viewInvoiceDetails(invoice.id);
    }

    /** Same destination the old Finalize/Pay buttons reached via
     *  [routerLink]="['/invoice-actions', { invoiceNo, id }]". */
    private goToInvoiceActions({ number, id }: InvoiceListItem): void {
        this.router.navigate(['/invoice-actions', { invoiceNo: number, id }]);
    }

    setPage(page: number): void {
        navigateProfilePaging(
            this.router,
            this.route,
            { page, pageSize: this.pageSize },
            this.defaultPageSize,
        );
    }

    setPageSize(pageSize: number): void {
        navigateProfilePaging(
            this.router,
            this.route,
            { page: 1, pageSize },
            this.defaultPageSize,
        );
    }

    viewInvoiceDetails(id?: string): void {
        if (!id) return;
        this.router.navigate(['../view-invoice', id], {
            relativeTo: this.route,
        });
    }

    isForfeited(invoice: InvoiceListItem): boolean {
        return !!invoice.forfeitureAcknowledgedAt;
    }

    /** Triangle tooltip for forfeited invoices — same copy as the old
     *  table's forfeited icon ("Forfeited 2026-07-09"). */
    private forfeitLabel(invoice: InvoiceListItem): string | undefined {
        if (!invoice.forfeitureAcknowledgedAt) return undefined;
        return `Forfeited ${fmtDate(invoice.forfeitureAcknowledgedAt)}`;
    }

    goToPaymentsList({ number }: InvoiceListItem): void {
        if (!number) return;
        this.router.navigate(['../invoice-payments'], {
            relativeTo: this.route,
            queryParams: { invoiceNumber: number },
        });
    }

    /** Wired to vb-search-input's per-keystroke valueChange; the debounced
     *  subject pipeline above is unchanged. */
    onSearchChange(searchText: string): void {
        this.searchText = searchText;
        this.searchedInvoice.next(searchText);
    }

    onViewShippingQuote(id?: string): void {
        if (!id) return;
        localStorage.setItem('invoiceShippingPanel', 'true');
        this.router.navigate(['../view-invoice', id], {
            relativeTo: this.route,
        });
    }

    ngOnDestroy(): void {
        this.mobile$.next();
        this.mobile$.complete();
        this.destroy$.next();
        this.destroy$.complete();
    }
}
